import { createContext, useContext, useReducer, useRef, useEffect } from 'react';
import { createDemoProject, createEmptyProject } from '@/utils/demoProject';
import { createStickmanDemo } from '@/utils/demoStickman';

const AnimationContext = createContext(null);

const initialState = {
  project: createDemoProject(),
  currentFrame: 0,
  selectedObjectId: null,
  selectedLayerId: "layer-chars",
  activeTool: "select",
  isPlaying: false,
  zoom: 1,
  panX: 0,
  panY: 0,
  onionSkin: false,
  clipboard: null,
  history: [],
  historyIndex: -1,
};

function pushHistory(state) {
  const newHistory = state.history.slice(0, state.historyIndex + 1);
  newHistory.push(JSON.parse(JSON.stringify(state.project)));
  if (newHistory.length > 50) newHistory.shift();
  return { history: newHistory, historyIndex: newHistory.length - 1 };
}

function reducer(state, action) {
  switch (action.type) {
    case 'SET_PROJECT':
      return { ...state, project: action.payload, selectedObjectId: null, selectedLayerId: null, currentFrame: 0 };

    case 'NEW_PROJECT': {
      const proj = createEmptyProject();
      return { ...state, project: proj, selectedObjectId: null, selectedLayerId: proj.layers[0]?.id, currentFrame: 0, isPlaying: false, ...pushHistory({ ...state, project: proj }) };
    }

    case 'LOAD_DEMO': {
      const demo = createDemoProject();
      return { ...state, project: demo, selectedObjectId: null, selectedLayerId: "layer-batelka", currentFrame: 0, isPlaying: false };
    }

    case 'LOAD_DEMO_2': {
      const demo2 = createStickmanDemo();
      return { ...state, project: demo2, selectedObjectId: null, selectedLayerId: "layer-stickman", currentFrame: 0, isPlaying: false };
    }

    case 'SET_FRAME':
      return { ...state, currentFrame: Math.max(0, Math.min(action.payload, state.project.totalFrames)) };

    case 'NEXT_FRAME': {
      const next = state.currentFrame + 1;
      return { ...state, currentFrame: next > state.project.totalFrames ? 0 : next };
    }

    case 'SET_PLAYING':
      return { ...state, isPlaying: action.payload };

    case 'SET_TOOL':
      return { ...state, activeTool: action.payload };

    case 'SET_ZOOM':
      return { ...state, zoom: Math.max(0.15, Math.min(4, action.payload)) };

    case 'SET_PAN':
      return { ...state, panX: action.payload.x, panY: action.payload.y };

    case 'ZOOM_AT': {
      const { newZoom } = action.payload;
      const clampedZoom = Math.max(0.15, Math.min(4, newZoom));
      return { ...state, zoom: clampedZoom };
    }

    case 'RESET_VIEW':
      return { ...state, zoom: 1, panX: 0, panY: 0 };

    case 'TOGGLE_ONION_SKIN':
      return { ...state, onionSkin: !state.onionSkin };

    case 'SELECT_OBJECT': {
      let layerId = state.selectedLayerId;
      if (action.payload) {
        for (const layer of state.project.layers) {
          if (layer.objects.find(o => o.id === action.payload)) { layerId = layer.id; break; }
        }
      }
      return { ...state, selectedObjectId: action.payload, selectedLayerId: layerId };
    }

    case 'SELECT_LAYER':
      return { ...state, selectedLayerId: action.payload };

    case 'ADD_LAYER': {
      const hist = pushHistory(state);
      const newLayer = { id: `layer-${Date.now()}`, name: `Layer ${state.project.layers.length + 1}`, visible: true, locked: false, objects: [] };
      return { ...state, project: { ...state.project, layers: [...state.project.layers, newLayer] }, selectedLayerId: newLayer.id, ...hist };
    }

    case 'REMOVE_LAYER': {
      if (state.project.layers.length <= 1) return state;
      const hist = pushHistory(state);
      const layers = state.project.layers.filter(l => l.id !== action.payload);
      return { ...state, project: { ...state.project, layers }, selectedLayerId: layers[0]?.id, selectedObjectId: null, ...hist };
    }

    case 'TOGGLE_LAYER_VISIBILITY': {
      const layers = state.project.layers.map(l => l.id === action.payload ? { ...l, visible: !l.visible } : l);
      return { ...state, project: { ...state.project, layers } };
    }

    case 'TOGGLE_LAYER_LOCK': {
      const layers = state.project.layers.map(l => l.id === action.payload ? { ...l, locked: !l.locked } : l);
      return { ...state, project: { ...state.project, layers } };
    }

    case 'RENAME_LAYER': {
      const layers = state.project.layers.map(l => l.id === action.payload.id ? { ...l, name: action.payload.name } : l);
      return { ...state, project: { ...state.project, layers } };
    }

    case 'ADD_OBJECT': {
      const hist = pushHistory(state);
      const targetLayerId = action.payload.layerId || state.selectedLayerId;
      const layers = state.project.layers.map(l => l.id === targetLayerId ? { ...l, objects: [...l.objects, action.payload.object] } : l);
      return { ...state, project: { ...state.project, layers }, selectedObjectId: action.payload.object.id, selectedLayerId: targetLayerId, ...hist };
    }

    case 'REMOVE_OBJECT': {
      const hist = pushHistory(state);
      const layers = state.project.layers.map(l => ({ ...l, objects: l.objects.filter(o => o.id !== action.payload) }));
      return { ...state, project: { ...state.project, layers }, selectedObjectId: state.selectedObjectId === action.payload ? null : state.selectedObjectId, ...hist };
    }

    case 'UPDATE_OBJECT': {
      const { id, props, autoKey } = action.payload;
      const frame = state.currentFrame;
      const layers = state.project.layers.map(l => ({
        ...l,
        objects: l.objects.map(o => {
          if (o.id !== id) return o;
          const updated = { ...o, ...props };
          if (autoKey && o.keyframes && o.keyframes.length > 0) {
            const animProps = {};
            const animatable = ['x', 'y', 'rotation', 'scaleX', 'scaleY', 'opacity'];
            for (const p of animatable) { if (props[p] !== undefined) animProps[p] = props[p]; }
            if (Object.keys(animProps).length > 0) {
              const existingIdx = updated.keyframes.findIndex(k => k.frame === frame);
              if (existingIdx >= 0) {
                updated.keyframes = updated.keyframes.map((k, i) => i === existingIdx ? { ...k, ...animProps } : k);
              } else {
                const baseKf = { frame };
                for (const p of animatable) {
                  baseKf[p] = animProps[p] !== undefined ? animProps[p] : (updated[p] ?? (p === 'opacity' ? 1 : p === 'scaleX' || p === 'scaleY' ? 1 : 0));
                }
                updated.keyframes = [...updated.keyframes, baseKf].sort((a, b) => a.frame - b.frame);
              }
            }
          }
          return updated;
        })
      }));
      return { ...state, project: { ...state.project, layers } };
    }

    case 'ADD_KEYFRAME': {
      const hist = pushHistory(state);
      const { objectId, frame } = action.payload;
      const layers = state.project.layers.map(l => ({
        ...l,
        objects: l.objects.map(o => {
          if (o.id !== objectId) return o;
          if (o.keyframes.find(k => k.frame === frame)) return o;
          const kf = { frame, x: o.x, y: o.y, rotation: o.rotation ?? 0, scaleX: o.scaleX ?? 1, scaleY: o.scaleY ?? 1, opacity: o.opacity ?? 1 };
          return { ...o, keyframes: [...o.keyframes, kf].sort((a, b) => a.frame - b.frame) };
        })
      }));
      return { ...state, project: { ...state.project, layers }, ...hist };
    }

    case 'REMOVE_KEYFRAME': {
      const hist = pushHistory(state);
      const { objectId, frame } = action.payload;
      const layers = state.project.layers.map(l => ({
        ...l, objects: l.objects.map(o => o.id !== objectId ? o : { ...o, keyframes: o.keyframes.filter(k => k.frame !== frame) })
      }));
      return { ...state, project: { ...state.project, layers }, ...hist };
    }

    case 'UNDO': {
      if (state.historyIndex <= 0) return state;
      const newIdx = state.historyIndex - 1;
      return { ...state, project: JSON.parse(JSON.stringify(state.history[newIdx])), historyIndex: newIdx };
    }

    case 'REDO': {
      if (state.historyIndex >= state.history.length - 1) return state;
      const newIdx = state.historyIndex + 1;
      return { ...state, project: JSON.parse(JSON.stringify(state.history[newIdx])), historyIndex: newIdx };
    }

    case 'SET_PROJECT_PROP':
      return { ...state, project: { ...state.project, ...action.payload } };

    case 'DUPLICATE_OBJECT': {
      const hist = pushHistory(state);
      let srcObj = null;
      let srcLayerId = null;
      for (const l of state.project.layers) {
        srcObj = l.objects.find(o => o.id === action.payload);
        if (srcObj) { srcLayerId = l.id; break; }
      }
      if (!srcObj) return state;
      const newObj = { ...JSON.parse(JSON.stringify(srcObj)), id: `obj-dup-${Date.now()}`, name: `${srcObj.name} copy`, x: srcObj.x + 30, y: srcObj.y + 30 };
      const layers = state.project.layers.map(l => l.id === srcLayerId ? { ...l, objects: [...l.objects, newObj] } : l);
      return { ...state, project: { ...state.project, layers }, selectedObjectId: newObj.id, ...hist };
    }

    case 'COPY_OBJECT': {
      if (!state.selectedObjectId) return state;
      let objToCopy = null;
      for (const l of state.project.layers) {
        objToCopy = l.objects.find(o => o.id === state.selectedObjectId);
        if (objToCopy) break;
      }
      if (!objToCopy) return state;
      return { ...state, clipboard: JSON.parse(JSON.stringify(objToCopy)) };
    }

    case 'PASTE_OBJECT': {
      if (!state.clipboard) return state;
      const hist = pushHistory(state);
      const targetLayerId = state.selectedLayerId || state.project.layers[0]?.id;
      if (!targetLayerId) return state;
      const pastedObj = {
        ...JSON.parse(JSON.stringify(state.clipboard)),
        id: `obj-paste-${Date.now()}`,
        name: `${state.clipboard.name} copy`,
        x: state.clipboard.x + 30,
        y: state.clipboard.y + 30,
      };
      const layers = state.project.layers.map(l =>
        l.id === targetLayerId ? { ...l, objects: [...l.objects, pastedObj] } : l
      );
      return { ...state, project: { ...state.project, layers }, selectedObjectId: pastedObj.id, ...hist };
    }

    case 'DELETE_OBJECT': {
      if (!state.selectedObjectId) return state;
      const hist = pushHistory(state);
      const layers = state.project.layers.map(l => ({
        ...l, objects: l.objects.filter(o => o.id !== state.selectedObjectId)
      }));
      return { ...state, project: { ...state.project, layers }, selectedObjectId: null, ...hist };
    }

    default:
      return state;
  }
}

export function AnimationProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const rafRef = useRef(null);
  const lastTimeRef = useRef(0);

  // requestAnimationFrame playback for smoother animation
  useEffect(() => {
    if (state.isPlaying) {
      const intervalMs = 1000 / state.project.fps;
      const animate = (timestamp) => {
        if (timestamp - lastTimeRef.current >= intervalMs) {
          dispatch({ type: 'NEXT_FRAME' });
          lastTimeRef.current = timestamp;
        }
        rafRef.current = requestAnimationFrame(animate);
      };
      rafRef.current = requestAnimationFrame(animate);
    } else {
      if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    }
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [state.isPlaying, state.project.fps]);

  // Auto-play on mount
  useEffect(() => {
    const timer = setTimeout(() => dispatch({ type: 'SET_PLAYING', payload: true }), 800);
    return () => clearTimeout(timer);
  }, []);

  // Keyboard shortcuts: Ctrl+C, Ctrl+V, Delete, Backspace
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Skip if user is typing in an input/textarea
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      if (e.ctrlKey || e.metaKey) {
        if (e.key === 'c') {
          e.preventDefault();
          dispatch({ type: 'COPY_OBJECT' });
        } else if (e.key === 'v') {
          e.preventDefault();
          dispatch({ type: 'PASTE_OBJECT' });
        }
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        dispatch({ type: 'DELETE_OBJECT' });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <AnimationContext.Provider value={{ state, dispatch }}>
      {children}
    </AnimationContext.Provider>
  );
}

export function useAnimation() {
  const ctx = useContext(AnimationContext);
  if (!ctx) throw new Error('useAnimation must be inside AnimationProvider');
  return ctx;
}

export function useSelectedObject() {
  const { state } = useAnimation();
  if (!state.selectedObjectId) return null;
  for (const layer of state.project.layers) {
    const obj = layer.objects.find(o => o.id === state.selectedObjectId);
    if (obj) return obj;
  }
  return null;
}
